/**
 * 📱 MOBILE-FIRST: Haptic Feedback Utility
 * Provides native-like haptic feedback for mobile interactions
 */

import { userGestureDetector } from './userGestureDetector';

export type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

interface HapticPattern {
  duration: number;
  pattern?: number[];
}

/**
 * Haptic feedback patterns for different interaction types
 */
const HAPTIC_PATTERNS: Record<HapticType, HapticPattern> = {
  light: { duration: 10 },
  medium: { duration: 20 },
  heavy: { duration: 30 },
  success: { duration: 15, pattern: [10, 50, 10] },
  warning: { duration: 25, pattern: [20, 100, 20] },
  error: { duration: 40, pattern: [30, 100, 30, 100, 30] },
  selection: { duration: 5 }
};

/**
 * Check if haptic feedback is supported
 */
export const isHapticSupported = (): boolean => {
  return 'vibrate' in navigator && typeof navigator.vibrate === 'function';
};

/**
 * Trigger haptic feedback with fallback for unsupported devices
 * V320.RELOAD.5: Gated behind user gesture to prevent Chrome intervention warnings
 */
export const triggerHaptic = (type: HapticType = 'light'): void => {
  if (!isHapticSupported()) {
    // Fallback: Visual feedback for devices without haptic support
    triggerVisualFeedback(type);
    return;
  }

  const pattern = HAPTIC_PATTERNS[type];

  try {
    // V320.RELOAD.5: Use safe vibrate that checks for user gesture
    const vibrated = pattern.pattern
      ? userGestureDetector.safeVibrate(pattern.pattern)
      : userGestureDetector.safeVibrate(pattern.duration);

    if (!vibrated) {
      // Fallback to visual if vibrate was blocked
      triggerVisualFeedback(type);
    }
  } catch (error) {
    console.warn('Haptic feedback failed:', error);
    triggerVisualFeedback(type);
  }
};

/**
 * Visual feedback fallback for devices without haptic support
 */
const triggerVisualFeedback = (type: HapticType): void => {
  // Create a subtle visual pulse effect
  const body = document.body;
  const originalFilter = body.style.filter;
  
  // Apply brief brightness change based on feedback type
  const brightness = {
    light: '1.02',
    medium: '1.05',
    heavy: '1.08',
    success: '1.03',
    warning: '1.04',
    error: '1.06',
    selection: '1.01'
  }[type];

  body.style.filter = `brightness(${brightness})`;
  body.style.transition = 'filter 0.05s ease';

  // Reset after brief moment
  setTimeout(() => {
    body.style.filter = originalFilter;
    body.style.transition = '';
  }, 50);
};

/**
 * Specialized haptic feedback for common mobile interactions
 */
export const hapticFeedback = {
  /**
   * Button tap feedback
   */
  buttonTap: () => triggerHaptic('light'),
  
  /**
   * Like/heart button feedback
   */
  like: () => triggerHaptic('success'),
  
  /**
   * Dislike/reject feedback
   */
  dislike: () => triggerHaptic('warning'),
  
  /**
   * Navigation tab selection
   */
  tabSelect: () => triggerHaptic('selection'),
  
  /**
   * Match notification feedback
   */
  match: () => triggerHaptic('success'),
  
  /**
   * Connection request sent
   */
  connectionSent: () => triggerHaptic('medium'),
  
  /**
   * Connection request approved
   */
  connectionApproved: () => triggerHaptic('success'),
  
  /**
   * Error or rejection feedback
   */
  error: () => triggerHaptic('error'),
  
  /**
   * Swipe gesture feedback
   */
  swipe: () => triggerHaptic('light'),
  
  /**
   * Long press feedback
   */
  longPress: () => triggerHaptic('heavy'),
  
  /**
   * Notification received
   */
  notification: () => triggerHaptic('medium'),
  
  /**
   * Photo upload success
   */
  photoUpload: () => triggerHaptic('success'),
  
  /**
   * Form submission
   */
  formSubmit: () => triggerHaptic('medium'),
  
  /**
   * Selection feedback
   */
  selection: () => triggerHaptic('selection')
};

/**
 * Hook for React components to use haptic feedback
 */
export const useHapticFeedback = () => {
  return {
    isSupported: isHapticSupported(),
    trigger: triggerHaptic,
    ...hapticFeedback
  };
};

export default hapticFeedback;

